import CrmContactSession from "./session/model-session";
import CustomFieldsProxy from "../../custom-fields-proxy";
import CrmContactAccessControl from "./access-control/access-control";
import Notes from "../../notes/notes";
import AttachmentsCollection from "../../attachment/attachments";
import initCrmContactAttachments from "./actions/init-attachments";
import {CrmContactMetadata} from "./@type";


export default class CrmContact extends CrmContactSession {

    public phone              : string | null
    public timezone           : string | null      // an IANA name, e.g. "Europe/Kyiv"
    // public email              : string | null
    public notes              : string | null
    public attachments          : AttachmentsCollection
    public metadata             : CrmContactMetadata
    declare customFieldsData   : CustomFieldsData | null

    constructor(data:any) {
        super(data);

        this.phone              = data['phone'];
        this.timezone           = data['timezone'];
        this.email              = data['email'];
        this.notes              = data['notes'];
        this.customFieldsData   = data['customFieldsData'];

        // the files of the contact, in upload order
        this.attachments        = initCrmContactAttachments(data['attachments']);

        this.setMetadata(_.get(data, 'metadata', {}));

        return new Proxy(this, CustomFieldsProxy);
    }

    // The metadata the server sends carries the pages as records; in memory metadata.pages is
    // the Notes collection the view works with. Everything else in metadata is kept as it is -
    // save() sends the whole object back, so a key dropped here is a key erased on the server.
    setMetadata(metadata: any) {
        const pages = _.get(metadata, 'pages', []);

        this.metadata = {
            ...metadata,
            pages: pages instanceof Notes ? pages : Notes.initFromJson(pages)
        }
    }

    // GOTCHA: a delta assigns metadata straight off the server record, which would leave
    // metadata.pages a plain array - every reader of contact.pages expects the collection.
    applyDelta(data:any):boolean {
        const result = super.applyDelta(data);

        if (_.has(data, 'metadata')) this.setMetadata(this.metadata);

        return result;
    }

    get pages(): Notes {
        return this.metadata.pages
    }

    // GOTCHA: the model is wrapped in CustomFieldsProxy, and a field only lands in
    // updatedFields - which is what save() sends - when it is assigned. The pages are changed
    // through the collection, so nothing marks metadata on its own.
    savePages() {
        this.updatedFields.add('metadata');

        return this.save();
    }

    // The metadata as it is stored - the pages collection back as its records. Never write this
    // into this.metadata: that is what the collection lives in.
    metadataToJSON(): JSONObject {
        return {...this.metadata, pages: this.pages.toJSON()}
    }

    // Same as savePages() - the files are changed through the collection, which assigns nothing,
    // so the field is marked here.
    saveAttachments() {
        this.updatedFields.add('attachments');

        return this.save();
    }

    // The whole list of files, which is what the PUT expects - a fileId missing from it is
    // deleted, [] removes them all.
    //
    // GOTCHA: AttachmentsCollection.toJSON() answers null for an empty collection, and a null
    // would leave the contact's files untouched - removing the last file would never stick.
    // It also drops uploads that have not finished, so a file still going up is not sent.
    attachmentsToJSON(): JSONArray {
        return this.attachments.toJSON() || []
    }

    // GOTCHA: ModelEntity.acl returns {}, and FieldProto.canEdit() reads
    // `model.acl ? model.acl.canChangeCustomFields : true` - a truthy but empty acl therefore
    // reports undefined, and every field that gates hover/click on canEdit() goes inert.
    get acl(): CrmContactAccessControl {
        return new CrmContactAccessControl(this)
    }

    save() {
        const params = {}

        this.updatedFields.forEach((fieldName) => {
            // @ts-ignore
            switch (fieldName) {
                // @ts-ignore
                case 'metadata'     : params[fieldName] = this.metadataToJSON();     break
                // @ts-ignore
                case 'attachments'  : params[fieldName] = this.attachmentsToJSON();  break
                // @ts-ignore
                default             : params[fieldName] = this[fieldName]
            }
        })

        // sent - each change goes once. attachments and metadata are whole lists, so replaying
        // them from a later save would undo what other views changed meanwhile: a file missing
        // from the list is deleted, and file changes send no session event to refresh this one
        this.updatedFields.clear()

        return gd.api.CRM.updateContact(this.id, params);
    }

}
